import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, Trash2, ExternalLink } from "lucide-react";

export default function PaymentLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payment-links");
      setLinks(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const copy = async (url, id) => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this payment link?")) return;
    try {
      await api.delete(`/payment-links/${id}`);
      toast.success("Link deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <main data-testid="payment-links-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gold">Atelier</div>
          <h1 className="font-display text-5xl tracking-tighter mt-3">Payment Links</h1>
        </div>
        <Link to="/merchant/links/new" data-testid="new-link-cta" className="btn-gold inline-flex items-center gap-2">
          <Plus size={16} /> New link
        </Link>
      </div>

      {loading ? (
        <div className="text-secondary-fg">Loading…</div>
      ) : links.length === 0 ? (
        <div className="border hairline p-16 text-center">
          <div className="font-display text-3xl">Compose your first link</div>
          <Link to="/merchant/links/new" className="btn-gold inline-block mt-8">Create now</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {links.map((l) => {
            const url = `${window.location.origin}/pay/${l.link_id}`;
            return (
              <div data-testid={`link-card-${l.link_id}`} key={l.link_id} className="lux-card p-7 flex flex-col gap-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-fg">{l.currency?.toUpperCase()}</div>
                    <div className="font-display text-4xl mt-2 gold-text tracking-tighter">{formatMoney(l.amount, l.currency)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-widest text-muted-fg">Paid</div>
                    <div className="font-mono text-sm mt-1">{l.times_paid || 0}×</div>
                  </div>
                </div>
                <div>
                  <div className="text-base font-medium">{l.title}</div>
                  {l.description && <div className="text-xs text-secondary-fg mt-1 line-clamp-2">{l.description}</div>}
                </div>
                <div className="font-mono text-xs text-muted-fg truncate" title={url}>{url}</div>
                <div className="flex items-center gap-2 mt-2 pt-3 border-t hairline">
                  <button data-testid={`copy-${l.link_id}`} onClick={() => copy(url, l.link_id)} className="flex-1 text-xs flex items-center justify-center gap-2 py-2 text-secondary-fg hover:text-gold transition-colors">
                    <Copy size={12} /> Copy
                  </button>
                  <a data-testid={`open-${l.link_id}`} href={url} target="_blank" rel="noreferrer" className="flex-1 text-xs flex items-center justify-center gap-2 py-2 text-secondary-fg hover:text-gold transition-colors">
                    <ExternalLink size={12} /> Open
                  </a>
                  <button data-testid={`delete-${l.link_id}`} onClick={() => remove(l.link_id)} className="flex-1 text-xs flex items-center justify-center gap-2 py-2 text-secondary-fg hover:text-red-400 transition-colors">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
